import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { PolarGrid, PolarAngleAxis, Radar, RadarChart, Dot } from "recharts";

import { User } from "../../../../types/types";

import { useUserDataFormatter } from "../../../User/hooks/useUserDataFormatter";

import { ReviewCriteria, reviewCriteriaCategoriesList } from "../../types/candidate";

const FULL_MARK = 100;
const RADAR_CHART_MEASUREMENTS = {
  outerRadius: 100,
  width: 820,
  height: 320,
  aspectRatio: 320 / 820,
  labelOffsetFromOuterRadius: 10,
  extraHorizontalOffset: 110,
  extraVerticalOffset: 20,
  polarRadius: [50, 100],
  chartRotationAngle: -45,
};

const CandidateOverview = ({ user, criteria }: { user: User; criteria: ReviewCriteria[] }) => {
  const { t } = useTranslation("goHire", { keyPrefix: "candidateReportPage" });

  const chartData = reviewCriteriaCategoriesList
    .map(({ categoryKey, criteriaKeys }) => {
      const categoryCriteria = criteria.filter((c) => criteriaKeys.includes(c.key));
      if (categoryCriteria.length === 0) return null;

      const avgRating = categoryCriteria.reduce((acc, curr) => acc + curr.rating, 0) / categoryCriteria.length;
      const percentage = (avgRating / 5) * 100;

      return {
        aspect: t(categoryKey),
        percentage: Math.round(percentage),
        fullMark: FULL_MARK,
      };
    })
    .filter(Boolean) as { aspect: string; percentage: number; fullMark: number }[];

  const { role } = useUserDataFormatter(user);

  const [chartDimensions, setChartDimensions] = useState({
    width: RADAR_CHART_MEASUREMENTS.width,
    height: RADAR_CHART_MEASUREMENTS.height,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateChartDimensions = () => {
      if (containerRef.current) {
        const innerWidth =
          containerRef.current.clientWidth > RADAR_CHART_MEASUREMENTS.width
            ? RADAR_CHART_MEASUREMENTS.width
            : containerRef.current.clientWidth - 48;
        setChartDimensions({
          width: innerWidth,
          height: innerWidth * RADAR_CHART_MEASUREMENTS.aspectRatio,
        });
      }
    };

    // Initial calculation
    updateChartDimensions();

    // Add resize listener
    window.addEventListener("resize", updateChartDimensions);

    // Cleanup
    return () => {
      window.removeEventListener("resize", updateChartDimensions);
    };
  }, []);

  // Calculate responsive scaling factor
  const scaleFactor = chartDimensions.width / RADAR_CHART_MEASUREMENTS.width;
  const responsiveOuterRadius = RADAR_CHART_MEASUREMENTS.outerRadius * scaleFactor;
  const responsivePolarRadius = RADAR_CHART_MEASUREMENTS.polarRadius.map((radius) => radius * scaleFactor);

  // Calculate responsive offsets
  const responsiveLabelOffset = RADAR_CHART_MEASUREMENTS.labelOffsetFromOuterRadius * scaleFactor;
  const responsiveHorizontalOffset = RADAR_CHART_MEASUREMENTS.extraHorizontalOffset * scaleFactor;
  const responsiveVerticalOffset = RADAR_CHART_MEASUREMENTS.extraVerticalOffset * scaleFactor;

  // Calculate responsive font sizes based on scale factor
  const responsiveAspectLabelFontSize = Math.max(16 * scaleFactor, 8);
  const responsiveAspectValueFontSize = Math.max(14 * scaleFactor, 8);

  return (
    <Box sx={styles.container} ref={containerRef}>
      <Typography sx={styles.sectionTitle}>{t("roleOverview", { role })}</Typography>
      <RadarChart
        outerRadius={responsiveOuterRadius}
        width={chartDimensions.width}
        height={chartDimensions.height}
        data={chartData}
        style={{ margin: "0 auto" }}
        startAngle={RADAR_CHART_MEASUREMENTS.chartRotationAngle}
        endAngle={RADAR_CHART_MEASUREMENTS.chartRotationAngle + 360}
      >
        <PolarGrid gridType="circle" stroke="#E7E7E7" polarRadius={responsivePolarRadius} />
        <PolarAngleAxis
          dataKey="aspect"
          tick={({ payload, x, y, cx, cy }) => {
            const angle = Math.atan2(y - cy, x - cx);
            const baseRadius = responsiveOuterRadius + responsiveLabelOffset;

            const baseX = cx + baseRadius * Math.cos(angle);
            const baseY = cy + baseRadius * Math.sin(angle);

            const horizontalOffset = Math.cos(angle) * responsiveHorizontalOffset;
            const verticalOffset = Math.sin(angle) * responsiveVerticalOffset;
            const labelX = baseX + horizontalOffset;
            const labelY = baseY + verticalOffset;

            return (
              <g>
                <text
                  x={labelX}
                  y={labelY}
                  dy={-10 * scaleFactor}
                  textAnchor="middle"
                  style={{
                    ...styles.aspectLabel,
                    fontSize: `${responsiveAspectLabelFontSize}px`,
                  }}
                >
                  {payload.value}
                </text>
                <text
                  x={labelX}
                  y={labelY}
                  dy={10 * scaleFactor}
                  textAnchor="middle"
                  style={{
                    ...styles.aspectValue,
                    fontSize: `${responsiveAspectValueFontSize}px`,
                  }}
                >
                  {chartData.find((item) => item.aspect === payload.value)?.percentage}%
                </text>
              </g>
            );
          }}
        />
        <Radar
          name="Skills"
          dataKey="percentage"
          stroke="#008B07"
          fill="#008B07"
          fillOpacity={0.2}
          dot={{
            r: 4 * scaleFactor,
            fill: "#008B07",
            strokeWidth: 2 * scaleFactor,
            stroke: "#FFFFFF",
          }}
        />
      </RadarChart>
    </Box>
  );
};

export default CandidateOverview;
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: "12px", sm: "14px", md: "16px" },
    padding: { xs: "16px", sm: "20px", md: "24px" },
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
  },
  sectionTitle: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: { xs: "14px", sm: "16px", md: "18px" },
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  aspectLabel: {
    color: "var(--Dark---800, #414141)",
    fontFamily: "Montserrat",
    fontStyle: "normal",
    fontWeight: 600,
  },
  aspectValue: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontStyle: "normal",
    fontWeight: 500,
  },
};
